/**
 * BulkCalibreAIEnhance — bulk AI enhancement for the currently-selected calibres
 * on the Curated Calibres page.
 *
 * Mirrors the reference tool's BulkAIEnrich (CuratedWatches/BulkAIEnrich.tsx)
 * pattern, applied to custom-calibre docs instead of references:
 *   - review → apply (config chosen up front, then a Run, then a per-cell grid;
 *     never a blind write)
 *   - writeMode 'fill' | 'overwrite' — default fills empty fields only
 *   - confidence threshold gates which cells start checked
 *   - per-cell checkboxes so the reviewer picks exactly what writes back
 *   - amber cells flag fields that already have a value (overwrite risk)
 *
 * One deliberate difference from BulkAIEnrich: the `enrichCalibre` callable
 * returns a single overall confidence per calibre (not per-field), so the row's
 * confidence gates every cell in that row. Everything else follows the same flow.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  enrichCalibreWithAI,
  getCalibreFamilyById,
  type CustomCalibreEntry,
} from '../../services/firebase';
import { tiersForBrand } from '../CuratedWatches/calibreTiers';
import {
  CALIBRE_ALWAYS_APPLY_KEYS,
  CALIBRE_DESCRIPTION_KEY,
  CALIBRE_ENRICH_FIELD_ORDER,
  CALIBRE_NAME_KEY,
  buildCalibrePatch,
  getCalibreExisting,
  getCalibreFieldLabel,
  getCalibreSuggestion,
  isCalibreRename,
} from '../../services/calibreEnrichFields';
import {
  defaultChecked,
  formatSuggestion,
  meetsThreshold,
  type EnrichThreshold,
  type EnrichWriteMode,
} from '../../services/bulkEnrichFields';
import { Button } from '../common/Button';
import { ColumnSelectAllCheckbox } from '../common/ColumnSelectAllCheckbox';
import { Modal } from '../common/Modal';

const CUSTOM_CALIBRES_COLLECTION = 'custom_calibres';

// Bounded parallelism — keeps us within the enrichCalibre Cloud Function /
// Anthropic rate limits (matches BulkAIEnrich's CONCURRENCY).
const CONCURRENCY = 4;

type RunStatus = 'queued' | 'running' | 'done' | 'failed';

interface CalibreRun {
  entry: CustomCalibreEntry;
  status: RunStatus;
  error?: string;
  specs: Record<string, unknown>;
  description?: string;
  /** AI-proposed canonical name (drives the always-apply rename + AKA demotion). */
  canonicalName?: string;
  /** Single overall confidence for the whole result ('high' | 'medium' | 'low'). */
  confidence: string;
}

type ApplyStatus = 'idle' | 'applying' | 'applied' | 'failed' | 'skipped';

// checked[calibreId][specKey] = whether that cell is selected for write-back.
type CheckedMap = Record<string, Record<string, boolean>>;

interface BulkCalibreAIEnhanceProps {
  isOpen: boolean;
  onClose: () => void;
  /** The currently-selected calibres to enhance. */
  calibres: CustomCalibreEntry[];
  /** Called after a write-back commits, so the host can refresh + clear selection. */
  onApplied: () => void;
}

export function BulkCalibreAIEnhance({
  isOpen,
  onClose,
  calibres,
  onApplied,
}: BulkCalibreAIEnhanceProps) {
  const [threshold, setThreshold] = useState<EnrichThreshold>('high');
  const [writeMode, setWriteMode] = useState<EnrichWriteMode>('fill');

  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState<CalibreRun[]>([]);
  const [checked, setChecked] = useState<CheckedMap>({});
  const [applyStatus, setApplyStatus] = useState<Record<string, ApplyStatus>>({});
  const [applying, setApplying] = useState(false);
  const [doneRunning, setDoneRunning] = useState<{ ok: number; failed: number; stopped: boolean } | null>(
    null,
  );
  const stopRef = useRef(false);

  const hasResults = useMemo(() => runs.some((r) => r.status === 'done'), [runs]);

  // Build the default checkbox map for a set of results given the current config.
  const computeDefaults = useCallback(
    (results: CalibreRun[], th: EnrichThreshold, wm: EnrichWriteMode): CheckedMap => {
      const next: CheckedMap = {};
      for (const r of results) {
        if (r.status !== 'done') continue;
        const row: Record<string, boolean> = {};
        for (const key of suggestedKeys(r)) {
          if (CALIBRE_ALWAYS_APPLY_KEYS.has(key)) {
            // Name + description default-checked regardless of the fill/overwrite
            // mode or an existing value — but still gated on the confidence
            // threshold, so a below-threshold rename/description starts unchecked.
            // The user can still tick it manually.
            row[key] = meetsThreshold(r.confidence, th);
            continue;
          }
          const hasExisting = getCalibreExisting(r.entry, key).trim() !== '';
          row[key] = defaultChecked({
            confidence: r.confidence,
            hasExisting,
            threshold: th,
            writeMode: wm,
          });
        }
        next[r.entry.id] = row;
      }
      return next;
    },
    [],
  );

  const updateRun = useCallback((index: number, update: Partial<CalibreRun>) => {
    setRuns((prev) => {
      const next = prev.slice();
      next[index] = { ...next[index], ...update };
      return next;
    });
  }, []);

  const handleRun = useCallback(async () => {
    if (calibres.length === 0) return;
    stopRef.current = false;
    setRunning(true);
    setDoneRunning(null);
    setChecked({});
    setApplyStatus({});

    // `collected` is the authoritative result array. Workers write into it by
    // index (single-threaded JS → safe between awaits) so after Promise.all we
    // compute defaults without reading back from React state.
    const collected: CalibreRun[] = calibres.map((entry) => ({
      entry,
      status: 'queued' as RunStatus,
      specs: {},
      confidence: 'medium',
    }));
    setRuns(collected.map((r) => ({ ...r })));

    let ok = 0;
    let failed = 0;
    let cursor = 0;

    // One family document per DISTINCT familyId across the whole run, not one
    // per calibre: a bulk pass over a manufacturer's catalogue is mostly
    // calibres sharing a handful of families, and the vocabulary is identical
    // for every member. A family that fails to load caches as empty so a single
    // bad id can't be retried once per calibre.
    const familyVocabCache = new Map<string, string[]>();
    const familyVocabFor = async (familyId: string | null | undefined): Promise<string[]> => {
      const id = familyId?.trim();
      if (!id) return [];
      const cached = familyVocabCache.get(id);
      if (cached) return cached;
      let names: string[] = [];
      try {
        const fam = await getCalibreFamilyById(id);
        names = fam?.tierNames ?? [];
      } catch (err) {
        console.warn('[BulkCalibreAIEnhance] family tier vocab load failed', id, err);
      }
      familyVocabCache.set(id, names);
      return names;
    };

    const worker = async () => {
      while (true) {
        if (stopRef.current) return;
        const i = cursor;
        cursor += 1;
        if (i >= calibres.length) return;

        const entry = calibres[i];
        updateRun(i, { status: 'running' });
        try {
          const result = await enrichCalibreWithAI(entry.name, {
            brandName: entry.brandName || undefined,
            // General tier names only: calibre enrichment never considers brand
            // tiers (James, 2026-10-29). No brand ⇒ the general ones.
            existingTierNames: tiersForBrand(entry.data?.tiers, undefined).map((t) => t.name),
            familyTierVocab: await familyVocabFor(entry.familyId),
            manufacturerId: entry.manufacturerId || undefined,
            calibreId: entry.id,
            familyId: entry.familyId || undefined,
            brandId: entry.brandId?.trim() || undefined,
          });
          const specs = result.specs || {};
          const description = result.description;
          const canonicalName = result.canonicalName;
          const confidence = (result.confidence || 'medium').toLowerCase();
          ok += 1;
          collected[i] = { ...collected[i], status: 'done', specs, description, canonicalName, confidence };
          updateRun(i, { status: 'done', specs, description, canonicalName, confidence });
        } catch (err) {
          failed += 1;
          const msg = err instanceof Error ? err.message : String(err);
          console.error(`AI calibre enhance failed for ${entry.name}:`, err);
          collected[i] = { ...collected[i], status: 'failed', error: msg };
          updateRun(i, { status: 'failed', error: msg });
        }
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

    const stopped = stopRef.current;
    setRunning(false);
    setDoneRunning({ ok, failed, stopped });
    setChecked(computeDefaults(collected, threshold, writeMode));

    if (stopped) toast(`Stopped — ${ok} enhanced, ${failed} failed`);
    else if (failed === 0) toast.success(`Enhanced ${ok} calibre${ok === 1 ? '' : 's'} — review below`);
    else toast(`Done — ${ok} enhanced, ${failed} failed`);
  }, [calibres, updateRun, computeDefaults, threshold, writeMode]);

  const handleStop = useCallback(() => {
    stopRef.current = true;
  }, []);

  // Re-seed defaults when the admin changes threshold / write-mode AFTER a run.
  const onThresholdChange = (t: EnrichThreshold) => {
    setThreshold(t);
    if (hasResults) setChecked(computeDefaults(runs, t, writeMode));
  };
  const onWriteModeChange = (m: EnrichWriteMode) => {
    setWriteMode(m);
    if (hasResults) setChecked(computeDefaults(runs, threshold, m));
  };

  const doneRuns = useMemo(() => runs.filter((r) => r.status === 'done'), [runs]);

  // Columns = union of suggestion keys across all done rows, in registry order.
  const columns = useMemo(() => {
    const present = new Set<string>();
    for (const r of doneRuns) for (const key of suggestedKeys(r)) present.add(key);
    return CALIBRE_ENRICH_FIELD_ORDER.filter((k) => present.has(k));
  }, [doneRuns]);

  const checkedCount = useMemo(() => {
    let n = 0;
    for (const row of Object.values(checked)) for (const v of Object.values(row)) if (v) n += 1;
    return n;
  }, [checked]);

  const setCell = useCallback((calibreId: string, key: string, value: boolean) => {
    setChecked((prev) => ({
      ...prev,
      [calibreId]: { ...(prev[calibreId] || {}), [key]: value },
    }));
  }, []);

  // Column header select-all: toggle every cell in one column that has a
  // proposed value, leaving all other columns untouched. Mode-agnostic — the
  // user's explicit "I want all of these", independent of writeMode / amber
  // defaults.
  const setColumn = useCallback(
    (key: string, value: boolean) => {
      setChecked((prev) => {
        const next: CheckedMap = { ...prev };
        for (const r of doneRuns) {
          if (!suggestedKeysSet(r).has(key)) continue;
          next[r.entry.id] = { ...(next[r.entry.id] || {}), [key]: value };
        }
        return next;
      });
    },
    [doneRuns],
  );

  // Per-column tally of proposed cells vs currently-checked ones, so the header
  // checkbox can render checked / unchecked / indeterminate.
  const columnState = useMemo(() => {
    const state: Record<string, { total: number; checkedCount: number }> = {};
    for (const key of columns) {
      let total = 0;
      let checkedInCol = 0;
      for (const r of doneRuns) {
        if (!suggestedKeysSet(r).has(key)) continue;
        total += 1;
        if (checked[r.entry.id]?.[key]) checkedInCol += 1;
      }
      state[key] = { total, checkedCount: checkedInCol };
    }
    return state;
  }, [columns, doneRuns, checked]);

  // Toolbar shortcuts operate over every (calibre, field) cell that has a suggestion.
  const bulkSet = useCallback(
    (predicate: (run: CalibreRun, key: string) => boolean) => {
      setChecked(() => {
        const next: CheckedMap = {};
        for (const r of doneRuns) {
          const row: Record<string, boolean> = {};
          for (const key of suggestedKeys(r)) row[key] = predicate(r, key);
          next[r.entry.id] = row;
        }
        return next;
      });
    },
    [doneRuns],
  );

  const selectAll = useCallback(() => bulkSet(() => true), [bulkSet]);
  const selectNone = useCallback(() => bulkSet(() => false), [bulkSet]);
  const selectAllHigh = useCallback(
    () => bulkSet((r) => r.confidence === 'high'),
    [bulkSet],
  );
  const selectAllHighMedium = useCallback(
    () => bulkSet((r) => r.confidence === 'high' || r.confidence === 'medium'),
    [bulkSet],
  );

  const handleApply = useCallback(async () => {
    setApplying(true);
    const status: Record<string, ApplyStatus> = {};
    let appliedCalibres = 0;
    let fieldCount = 0;
    let failedCalibres = 0;

    for (const r of doneRuns) {
      const row = checked[r.entry.id] || {};
      const acceptedKeys = Object.entries(row)
        .filter(([, on]) => on)
        .map(([key]) => key);
      const patch = buildCalibrePatch(r.entry, acceptedKeys, r);
      const cells = Object.keys(patch).length;
      if (cells === 0) {
        status[r.entry.id] = 'skipped';
        continue;
      }
      patch.updatedAt = serverTimestamp();
      status[r.entry.id] = 'applying';
      setApplyStatus({ ...status });
      try {
        await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, r.entry.id), patch);
        status[r.entry.id] = 'applied';
        appliedCalibres += 1;
        // Count accepted cells (not patch keys — one cell can write >1 path).
        fieldCount += acceptedKeys.length;
      } catch (err) {
        console.error(`Apply failed for ${r.entry.name}:`, err);
        status[r.entry.id] = 'failed';
        failedCalibres += 1;
      }
      setApplyStatus({ ...status });
    }

    setApplying(false);
    if (failedCalibres === 0) {
      toast.success(
        `Applied ${fieldCount} field${fieldCount === 1 ? '' : 's'} across ${appliedCalibres} calibre${appliedCalibres === 1 ? '' : 's'}`,
      );
    } else {
      toast.error(`Applied ${appliedCalibres} calibre${appliedCalibres === 1 ? '' : 's'}, ${failedCalibres} failed`);
    }
    onApplied();
  }, [doneRuns, checked, onApplied]);

  const processedCount = useMemo(
    () => runs.filter((r) => r.status === 'done' || r.status === 'failed').length,
    [runs],
  );

  const busy = running || applying;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!busy) onClose(); }}
      title={`AI Calibre Enhancement — ${calibres.length} selected`}
      size="full"
    >
      <div className="space-y-5">
        {/* Config */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Confidence threshold
              </legend>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="calEnrichThreshold"
                  checked={threshold === 'high'}
                  disabled={busy}
                  onChange={() => onThresholdChange('high')}
                />
                <span>High only</span>
                <span className="text-xs text-gray-400">(default)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="calEnrichThreshold"
                  checked={threshold === 'highMedium'}
                  disabled={busy}
                  onChange={() => onThresholdChange('highMedium')}
                />
                <span>High + Medium</span>
              </label>
            </fieldset>

            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Write-back mode
              </legend>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="calEnrichWriteMode"
                  checked={writeMode === 'fill'}
                  disabled={busy}
                  onChange={() => onWriteModeChange('fill')}
                />
                <span>Fill empty fields only</span>
                <span className="text-xs text-gray-400">(default)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="calEnrichWriteMode"
                  checked={writeMode === 'overwrite'}
                  disabled={busy}
                  onChange={() => onWriteModeChange('overwrite')}
                />
                <span>Overwrite existing values</span>
              </label>
            </fieldset>
          </div>
          <p className="text-xs text-gray-500">
            The threshold + mode decide which cells start <em>checked</em> in the review grid. Every
            proposed value is shown regardless — you can always check a cell manually. In “fill empty”
            mode, fields that already have a value are highlighted and start unchecked.{' '}
            <strong>Canonical name and description start checked whenever they meet the confidence
            threshold</strong> (independent of the fill/overwrite mode); on a rename, the old name is
            moved into the calibre’s “also known as” aliases.
          </p>

          <div className="flex items-center gap-2">
            {!running && !hasResults && (
              <Button variant="primary" size="sm" onClick={handleRun} disabled={calibres.length === 0}>
                Run AI on {calibres.length} calibre{calibres.length === 1 ? '' : 's'}
              </Button>
            )}
            {!running && hasResults && (
              <Button variant="secondary" size="sm" onClick={handleRun} disabled={applying}>
                Re-run
              </Button>
            )}
            {running && (
              <Button variant="danger" size="sm" onClick={handleStop}>
                Stop
              </Button>
            )}
            {running && (
              <span className="text-xs text-gray-500">
                Processing {processedCount} of {runs.length}…
              </span>
            )}
          </div>

          {running && runs.length > 0 && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-1.5 rounded-full bg-indigo-600 transition-all"
                style={{ width: `${(processedCount / runs.length) * 100}%` }}
              />
            </div>
          )}

          {doneRunning && (
            <div
              className={`rounded-md px-3 py-2 text-sm ${
                doneRunning.failed === 0 && !doneRunning.stopped
                  ? 'bg-green-50 text-green-800'
                  : 'bg-amber-50 text-amber-800'
              }`}
            >
              {doneRunning.stopped ? 'Stopped. ' : 'Enhancement complete. '}
              {doneRunning.ok} enhanced, {doneRunning.failed} failed.
            </div>
          )}
        </div>

        {/* Review grid */}
        {hasResults && columns.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Select:</span>
                <button type="button" className="text-indigo-600 hover:underline disabled:text-gray-300" onClick={selectAll} disabled={applying}>All</button>
                <span className="text-gray-300">·</span>
                <button type="button" className="text-indigo-600 hover:underline disabled:text-gray-300" onClick={selectNone} disabled={applying}>None</button>
                <span className="text-gray-300">·</span>
                <button type="button" className="text-indigo-600 hover:underline disabled:text-gray-300" onClick={selectAllHigh} disabled={applying}>All high-confidence</button>
                <span className="text-gray-300">·</span>
                <button type="button" className="text-indigo-600 hover:underline disabled:text-gray-300" onClick={selectAllHighMedium} disabled={applying}>All high + medium</button>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApply}
                disabled={applying || checkedCount === 0}
                loading={applying}
              >
                Apply {checkedCount} field{checkedCount === 1 ? '' : 's'}
              </Button>
            </div>

            <div className="overflow-auto border border-gray-200 rounded-md max-h-[28rem]">
              <table className="text-xs border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th className="sticky left-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                      Calibre
                    </th>
                    {columns.map((key) => {
                      const col = columnState[key] ?? { total: 0, checkedCount: 0 };
                      return (
                        <th
                          key={key}
                          className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                        >
                          <span className="flex items-center gap-1.5">
                            <ColumnSelectAllCheckbox
                              total={col.total}
                              checkedCount={col.checkedCount}
                              onToggle={(value) => setColumn(key, value)}
                              disabled={applying}
                              label={getCalibreFieldLabel(key)}
                            />
                            {getCalibreFieldLabel(key)}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {doneRuns.map((r) => {
                    const aStatus = applyStatus[r.entry.id] || 'idle';
                    return (
                      <tr key={r.entry.id} className="align-top">
                        <th className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-2 py-1.5 text-left font-mono font-normal text-gray-700 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <ConfidenceDot level={r.confidence} />
                            <span>{r.entry.name}</span>
                            <ApplyDot status={aStatus} />
                          </div>
                          {r.entry.brandName && (
                            <div className="font-sans text-[10px] text-gray-400 truncate max-w-[10rem]">
                              {r.entry.brandName}
                            </div>
                          )}
                        </th>
                        {columns.map((key) => {
                          const keys = suggestedKeysSet(r);
                          if (!keys.has(key)) {
                            return (
                              <td key={key} className="border-b border-gray-100 px-2 py-1.5 text-gray-300">
                                —
                              </td>
                            );
                          }
                          const existing = getCalibreExisting(r.entry, key);
                          const conflict = existing.trim() !== '';
                          const isChecked = !!checked[r.entry.id]?.[key];
                          const value = getCalibreSuggestion(r, key);
                          return (
                            <td
                              key={key}
                              className={`border-b border-gray-100 px-2 py-1.5 ${conflict ? 'bg-amber-50' : ''}`}
                            >
                              <label className="flex items-start gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="mt-0.5 shrink-0"
                                  checked={isChecked}
                                  disabled={applying}
                                  onChange={(e) => setCell(r.entry.id, key, e.target.checked)}
                                />
                                <span className="min-w-0 max-w-[16rem]">
                                  {formatSuggestion(value) ? (
                                    <ExpandableText text={formatSuggestion(value)} className="text-gray-800 break-words" />
                                  ) : (
                                    <span className="text-gray-300">∅</span>
                                  )}
                                  {conflict && (
                                    <span className="block mt-0.5">
                                      <ExpandableText
                                        text={`existing: ${existing}`}
                                        className="text-[10px] text-amber-700 break-words"
                                      />
                                    </span>
                                  )}
                                </span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-gray-400">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle" /> high ·{' '}
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 align-middle" /> medium ·{' '}
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300 align-middle" /> low (per-calibre
              confidence). Amber cells already have a value (overwrite risk).
            </p>
          </div>
        )}

        {/* Failures */}
        {runs.some((r) => r.status === 'failed') && (
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer">
              {runs.filter((r) => r.status === 'failed').length} calibre(s) failed to enhance
            </summary>
            <ul className="mt-1 space-y-0.5">
              {runs
                .filter((r) => r.status === 'failed')
                .map((r) => (
                  <li key={r.entry.id} className="font-mono">
                    {r.entry.name}
                    <span className="ml-2 font-sans text-red-600">
                      {r.error ? r.error.slice(0, 80) : 'Failed'}
                    </span>
                  </li>
                ))}
            </ul>
          </details>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Column keys a done run actually proposes a value for: the canonical-name column
 *  only on a real rename, the description column when the result carried a
 *  description, and every spec key the result returned. */
function suggestedKeys(r: CalibreRun): string[] {
  const keys: string[] = [];
  for (const key of CALIBRE_ENRICH_FIELD_ORDER) {
    if (key === CALIBRE_NAME_KEY) {
      if (isCalibreRename(r.entry, r.canonicalName)) keys.push(key);
    } else if (key === CALIBRE_DESCRIPTION_KEY) {
      if ((r.description || '').trim() !== '') keys.push(key);
    } else if (key in r.specs) {
      keys.push(key);
    }
  }
  return keys;
}

function suggestedKeysSet(r: CalibreRun): Set<string> {
  return new Set(suggestedKeys(r));
}

// Two-line clamp applied via inline style, not the Tailwind `line-clamp-2`
// utility. The clamp needs `display: -webkit-box`, but any co-applied `block`
// utility emits `display: block` LATER in the stylesheet and, at equal
// specificity, wins — silently disabling the clamp so the cell renders full
// height. Inline styles beat any class regardless of emission order, so the
// clamp can't be broken by a stray `block` again.
const CLAMP_STYLE: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

/**
 * Renders potentially-long cell text clamped to 2 lines, with an inline
 * Show more / Show less toggle. Mirrors BulkAIEnrich's ExpandableText so the
 * grid stays scannable. Purely visual — the underlying value is never altered.
 */
function ExpandableText({ text, className = '' }: { text: string; className?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Measure only while clamped — expanded, scrollHeight === clientHeight, so
    // re-measuring would wrongly clear `overflowing` and hide the toggle.
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <span className="block">
      <span
        ref={ref}
        className={`block ${className}`}
        style={expanded ? undefined : CLAMP_STYLE}
        title={text}
      >
        {text}
      </span>
      {(overflowing || expanded) && (
        <button
          type="button"
          className="mt-0.5 text-[10px] font-medium text-indigo-600 hover:underline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </span>
  );
}

function ConfidenceDot({ level }: { level: string }) {
  const color =
    level === 'high' ? 'bg-green-500' : level === 'medium' ? 'bg-amber-400' : 'bg-gray-300';
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`}
      title={`${level} confidence`}
    />
  );
}

function ApplyDot({ status }: { status: ApplyStatus }) {
  switch (status) {
    case 'applying':
      return <span className="text-blue-600 text-[10px]">…</span>;
    case 'applied':
      return <span className="text-green-600 text-[10px]" title="Applied">✓</span>;
    case 'failed':
      return <span className="text-red-600 text-[10px]" title="Apply failed">✗</span>;
    case 'skipped':
      return <span className="text-gray-300 text-[10px]" title="Nothing checked">–</span>;
    default:
      return null;
  }
}
